import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, FileText, Download } from 'lucide-react';
import CustomModal from '../CustomModal/CustumModal';

const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i.test(url);
const isPdfUrl = (url) => /\.pdf(\?|$)/i.test(url);
const fileNameOf = (url) => {
  if (!url) return 'File';
  try {
    return decodeURIComponent(url.split('/').pop().split('?')[0]) || 'File';
  } catch {
    return 'File';
  }
};

function ProofGalleryModal({ isOpen, onClose, urls = [] }) {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (isOpen) setActiveIdx(0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') setActiveIdx((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setActiveIdx((i) => Math.min(urls.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, urls.length]);

  if (!isOpen || urls.length === 0) return null;

  const activeUrl = urls[activeIdx];
  const activeIsImage = isImageUrl(activeUrl);
  const activeIsPdf = isPdfUrl(activeUrl);

  return (
    <CustomModal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Expense Proofs</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {activeIdx + 1} of {urls.length} · {fileNameOf(activeUrl)}
            </p>
          </div>
          <div className="flex items-center gap-2 mr-8">
            <a
              href={activeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open
            </a>
            <a
              href={activeUrl}
              download
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </a>
          </div>
        </div>

        {/* Main viewer */}
        <div className="relative bg-gray-50 flex items-center justify-center min-h-[320px] sm:min-h-[460px] max-h-[60vh]">
          {urls.length > 1 && (
            <button
              type="button"
              onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
              disabled={activeIdx === 0}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-white shadow rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
          )}

          {activeIsImage && (
            <img
              src={activeUrl}
              alt={fileNameOf(activeUrl)}
              className="max-h-[60vh] max-w-full object-contain"
            />
          )}

          {activeIsPdf && (
            <iframe
              src={activeUrl}
              title={fileNameOf(activeUrl)}
              className="w-full h-[60vh] bg-white"
            />
          )}

          {!activeIsImage && !activeIsPdf && (
            <div className="flex flex-col items-center gap-3 text-gray-500 p-8 text-center">
              <FileText className="w-12 h-12" />
              <p className="text-sm">Preview not available for this file type.</p>
              <a
                href={activeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm font-medium"
              >
                Open file in new tab
              </a>
            </div>
          )}

          {urls.length > 1 && (
            <button
              type="button"
              onClick={() => setActiveIdx((i) => Math.min(urls.length - 1, i + 1))}
              disabled={activeIdx === urls.length - 1}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-white shadow rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          )}
        </div>

        {/* Thumbnails strip */}
        {urls.length > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-white">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {urls.map((url, idx) => {
                const img = isImageUrl(url);
                const pdf = isPdfUrl(url);
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={`${url}-${idx}`}
                    type="button"
                    onClick={() => setActiveIdx(idx)}
                    className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      isActive
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                    title={fileNameOf(url)}
                  >
                    {img ? (
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    ) : pdf ? (
                      <div className="w-full h-full flex items-center justify-center bg-red-50">
                        <FileText className="w-6 h-6 text-red-500" />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <FileText className="w-6 h-6 text-gray-500" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
}

export default ProofGalleryModal;
